// src/pages/business/LivePOSPage.tsx
// Full-screen, standalone Point-of-Sale experience

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search, X, Package, ShoppingCart, Grid2X2,
  LayoutGrid, List, ArrowLeft, RefreshCw, Loader2,
  Lock, Unlock, ScanBarcode, History, Zap
} from 'lucide-react';
import { extractImageUrls } from '@/config/media';
import { useBusinessStore } from '@/stores/useBusinessStore';
import { usePOSCartStore } from '@/stores/usePOSCartStore';
import { POSCartSidebar } from '@/components/business/livepos/POSCartSidebar';
import { useToast } from '@/contexts/ToastContext';
import { ThemeToggle } from '@/components/common/ThemeToggle';

type ColCount = 1 | 2 | 3 | 4;

const stockColor = (stock: number, threshold: number) =>
  stock === 0 ? '#ef4444' : stock <= threshold ? '#f59e0b' : '#10b981';

// ── Product Tile ────────────────────────────────────────────────────────────
const POSProductTile: React.FC<{
  item: any;
  cols: ColCount;
  onAdd: () => void;
  locked: boolean;
  onOpenDetail: () => void;
}> = ({ item, cols, onAdd, locked, onOpenDetail }) => {
  const thumb = extractImageUrls(item.media_urls)[0] || item.image_url || null;
  const stock = item.quantity_in_stock ?? 0;
  const threshold = item.low_stock_threshold ?? 5;
  const sc = stockColor(stock, threshold);
  const outOfStock = stock === 0;

  if (cols === 1) {
    return (
      <button
        onClick={() => locked ? onAdd() : onOpenDetail()}
        disabled={outOfStock}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left relative active:scale-[0.98] ${
          outOfStock
            ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'
            : 'border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card hover:bg-light-bg dark:hover:bg-dark-bg hover:border-primary/40 dark:hover:border-golden/40 cursor-pointer'
        }`}
      >
        <div className="relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
          {thumb ? (
            <img src={thumb} alt={item.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={22} className="text-gray-300 dark:text-gray-600" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-light-text dark:text-dark-text line-clamp-1">{item.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-sm font-bold text-primary dark:text-golden">
              KES {item.price?.toLocaleString()}
            </span>
            <span className="text-xs px-1.5 py-0.5 rounded-md font-medium" style={{ color: sc, backgroundColor: sc + '20' }}>
              {stock === 0 ? 'Out' : `×${stock}`}
            </span>
          </div>
        </div>
        {!outOfStock && (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary dark:bg-golden flex items-center justify-center shadow-md">
            <span className="text-white font-bold text-lg leading-none">+</span>
          </div>
        )}
      </button>
    );
  }

  // Grid card
  const tileSize = cols === 2 ? 'text-sm' : cols === 3 ? 'text-xs' : 'text-[10px]';
  return (
    <div
      onClick={() => !outOfStock && (locked ? onAdd() : onOpenDetail())}
      className={`relative rounded-xl overflow-hidden border transition-all select-none ${
        outOfStock
          ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700'
          : 'border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card hover:shadow-lg hover:border-primary/40 dark:hover:border-golden/40 cursor-pointer active:scale-[0.97]'
      }`}
      style={{ width: cols === 2 ? '48%' : cols === 3 ? '31.5%' : '23%' }}
    >
      {/* Image */}
      <div className="aspect-square bg-gray-100 dark:bg-gray-800 relative">
        {thumb ? (
          <img src={thumb} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={cols === 4 ? 22 : 30} className="text-gray-300 dark:text-gray-600" />
          </div>
        )}
        {/* Stock badge */}
        <div
          className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold"
          style={{ color: sc, backgroundColor: sc + '25' }}
        >
          {stock === 0 ? 'OUT' : `×${stock}`}
        </div>
        {/* Add button */}
        {!outOfStock && (
          <button
            onClick={(e) => { e.stopPropagation(); onAdd(); }}
            className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-primary dark:bg-golden flex items-center justify-center shadow-lg"
          >
            <span className="text-white font-bold text-base leading-none">+</span>
          </button>
        )}
      </div>
      {/* Info */}
      <div className="px-2 py-1.5">
        <p className={`font-semibold line-clamp-2 text-light-text dark:text-dark-text ${tileSize}`}>{item.name}</p>
        <p className={`font-bold text-primary dark:text-golden mt-0.5 ${tileSize}`}>
          KES {item.price?.toLocaleString()}
        </p>
      </div>
    </div>
  );
};

// ── Main POS Page ────────────────────────────────────────────────────────────
export const LivePOSPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const toastCtx = useToast();

  const { products, fetchProducts, activeBusiness } = useBusinessStore();
  const { addItem, scanItem } = usePOSCartStore();

  const [loading, setLoading] = useState(products.length === 0);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [cols, setCols] = useState<ColCount>(3);
  const [isPosLocked, setIsPosLocked] = useState(true);

  // Barcode scanner buffer
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const lastKeystrokeRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    if (!businessId) return;

    if (products.length === 0) {
      setLoading(true);
      fetchProducts(businessId).finally(() => {
        if (!cancelled) setLoading(false);
      });
    } else {
      setLoading(false);
      fetchProducts(businessId);
    }

    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [businessId, fetchProducts]);

  // Barcode scanner listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) return;
      const now = Date.now();
      if (e.key === 'Enter') {
        if (barcodeBuffer.length > 3) handleScan(barcodeBuffer);
        setBarcodeBuffer('');
      } else if (e.key.length === 1) {
        setBarcodeBuffer(prev =>
          now - lastKeystrokeRef.current > 100 ? e.key : prev + e.key
        );
        lastKeystrokeRef.current = now;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [barcodeBuffer]);

  const handleScan = async (code: string) => {
    if (!businessId) return;
    try {
      toastCtx.info('Scanning…', code);
      await scanItem(businessId, code);
    } catch (err: any) {
      toastCtx.error('Not found', err.response?.data?.error || 'Item not found');
    }
  };

  const addToCart = useCallback(async (product: any) => {
    if (!businessId) return;
    try {
      await addItem(businessId, product, 1);
    } catch {
      // optimistic — store handles silently
    }
  }, [businessId, addItem]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q)
    );
  }, [products, search]);

  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category).filter(Boolean))];
    return ['All', ...cats];
  }, [products]);

  const [activeCategory, setActiveCategory] = useState('All');
  const categoryFiltered = useMemo(() => {
    if (activeCategory === 'All') return filtered;
    return filtered.filter(p => p.category === activeCategory);
  }, [filtered, activeCategory]);

  return (
    <div className="flex flex-col h-[100dvh] w-full overflow-hidden bg-light-bg dark:bg-dark-bg">

      {/* ── POS App Header ─────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border shadow-sm">
        <div className="flex items-center gap-2 px-3 py-2.5">

          {/* Back */}
          <button
            onClick={() => navigate(businessId ? `/business/${businessId}` : '/business')}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            title="Back to dashboard"
          >
            <ArrowLeft size={20} className="text-primary dark:text-golden" />
          </button>

          {/* POS Brand */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="w-7 h-7 rounded-lg bg-primary dark:bg-golden flex items-center justify-center">
              <Zap size={15} className="text-white" />
            </div>
            <div className="hidden sm:block">
              <p className="text-xs font-extrabold text-light-text dark:text-dark-text leading-none tracking-tight">LIVE POS</p>
              <p className="text-[9px] text-light-subtle dark:text-dark-subtle leading-none">{activeBusiness?.name || 'Point of Sale'}</p>
            </div>
          </div>

          <div className="w-px h-5 bg-light-border dark:dark:border-dark-border mx-1 flex-shrink-0" />

          {/* Search */}
          <div className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-xl border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg">
            <Search size={15} className="text-light-subtle dark:text-dark-subtle flex-shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${products.length} products…`}
              className="flex-1 bg-transparent text-sm text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none"
            />
            {search && (
              <button onClick={() => setSearch('')}>
                <X size={14} className="text-light-subtle dark:text-dark-subtle" />
              </button>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => navigate(`/business/${businessId}/sales`)}
              className="hidden md:flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-light-border dark:border-dark-border text-xs font-semibold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition"
              title="Sales History"
            >
              <History size={15} />
              <span className="hidden lg:inline">History</span>
            </button>

            {/* Lock/Unlock POS mode */}
            <button
              onClick={() => setIsPosLocked(!isPosLocked)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition ${
                isPosLocked
                  ? 'bg-primary/10 border-primary/30 text-primary dark:bg-golden/10 dark:border-golden/30 dark:text-golden'
                  : 'border-light-border dark:border-dark-border text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg'
              }`}
              title={isPosLocked ? 'POS Mode: Tap adds to cart' : 'Browse Mode: Tap opens details'}
            >
              {isPosLocked ? <Lock size={14} /> : <Unlock size={14} />}
              <span className="hidden lg:inline">{isPosLocked ? 'POS' : 'Browse'}</span>
            </button>

            {/* Column toggle */}
            <button
              onClick={() => setCols(cols === 1 ? 2 : cols === 2 ? 3 : cols === 3 ? 4 : 1)}
              className="p-1.5 rounded-lg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition"
              title="Toggle grid columns"
            >
              {cols === 1 ? <List size={16} /> : cols === 2 ? <Grid2X2 size={16} /> : <LayoutGrid size={16} />}
            </button>

            {/* Barcode scanner status */}
            <div
              className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800"
              title="Barcode Listening Mode Active"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <ScanBarcode size={13} className="text-emerald-600 dark:text-emerald-400" />
            </div>

            <ThemeToggle />
          </div>
        </div>

        {/* Category filter strip */}
        {categories.length > 1 && (
          <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                  activeCategory === cat
                    ? 'bg-primary dark:bg-golden text-white shadow-sm'
                    : 'bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Stats bar */}
        {!loading && products.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-1.5 border-t border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg text-xs text-light-subtle dark:text-dark-subtle">
            <span><span className="font-bold text-primary dark:text-golden">{categoryFiltered.length}</span> items</span>
            <span className="w-px h-3 bg-light-border dark:bg-dark-border" />
            <span>
              <span className="font-bold text-red-500">
                {products.filter(p => p.quantity_in_stock === 0).length}
              </span> out of stock
            </span>
            <span className="w-px h-3 bg-light-border dark:bg-dark-border" />
            <span>
              <span className="font-bold text-amber-500">
                {products.filter(p => p.quantity_in_stock > 0 && p.quantity_in_stock <= (p.low_stock_threshold ?? 5)).length}
              </span> low stock
            </span>
            <button
              onClick={async () => {
                if (!businessId) return;
                setRefreshing(true);
                await fetchProducts(businessId);
                setRefreshing(false);
              }}
              className="ml-auto flex items-center gap-1 hover:text-light-text dark:hover:text-dark-text"
              disabled={refreshing}
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* ── Main Split Layout ────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left — Product Grid */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-3">
            {loading && (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <Loader2 size={36} className="animate-spin text-primary dark:text-golden" />
                <p className="text-sm text-light-subtle dark:text-dark-subtle">Loading products…</p>
              </div>
            )}

            {!loading && categoryFiltered.length === 0 && (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <Package size={56} className="text-light-subtle dark:text-dark-subtle opacity-40" />
                <p className="font-semibold text-light-subtle dark:text-dark-subtle">
                  {search ? 'No products found' : 'No products yet'}
                </p>
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="text-sm text-primary dark:text-golden underline"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}

            {!loading && categoryFiltered.length > 0 && (
              cols === 1 ? (
                <div className="space-y-2">
                  {categoryFiltered.map(item => (
                    <POSProductTile
                      key={item.id}
                      item={item}
                      cols={1}
                      locked={isPosLocked}
                      onAdd={() => addToCart(item)}
                      onOpenDetail={() => navigate(`/business/${businessId}/products/${item.id}`)}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {categoryFiltered.map(item => (
                    <POSProductTile
                      key={item.id}
                      item={item}
                      cols={cols}
                      locked={isPosLocked}
                      onAdd={() => addToCart(item)}
                      onOpenDetail={() => navigate(`/business/${businessId}/products/${item.id}`)}
                    />
                  ))}
                </div>
              )
            )}
          </div>
        </div>

        {/* Right — Cart Sidebar (always visible on desktop) */}
        <div
          className="hidden lg:flex flex-shrink-0 border-l border-light-border dark:border-dark-border shadow-xl"
          style={{ width: '380px' }}
        >
          <POSCartSidebar businessId={businessId!} />
        </div>

        {/* Mobile: Floating Cart Button */}
        <div className="lg:hidden">
          <MobileCartButton businessId={businessId!} />
        </div>
      </div>
    </div>
  );
};

// ── Mobile floating cart button + bottom sheet ───────────────────────────────
const MobileCartButton: React.FC<{ businessId: string }> = ({ businessId }) => {
  const { cart } = usePOSCartStore();
  const [open, setOpen] = useState(false);
  const itemCount = cart?.cart_items?.length ?? 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-primary dark:bg-golden text-white rounded-full shadow-2xl shadow-primary/40 dark:shadow-golden/40"
      >
        <ShoppingCart size={20} />
        {itemCount > 0 && (
          <span className="font-bold">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
        )}
        {itemCount === 0 && <span className="font-bold">Checkout</span>}
      </button>

      {/* Bottom Sheet */}
      {open && (
        <div className="fixed inset-0 z-[100] flex flex-col">
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
          <div className="bg-light-card dark:bg-dark-card rounded-t-3xl shadow-2xl" style={{ height: '80vh' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-light-border dark:border-dark-border">
              <h2 className="font-bold text-lg text-light-text dark:text-dark-text">Current Sale</h2>
              <button onClick={() => setOpen(false)} className="p-2 rounded-xl hover:bg-light-bg dark:hover:bg-dark-bg">
                <X size={20} className="text-light-text dark:text-dark-text" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto h-full">
              <POSCartSidebar businessId={businessId} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LivePOSPage;
